import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthProvider";
import Sidebar from "./SideBar";
import Header from "./Header";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { isLoggedin } from "../lib/isLoggedin";
import { useMeetingStore } from "../store/meeting.store";
import Loader from "../pages/protected/profile/components/Loader";
import { useSessionGuard } from "../hooks/useSessionGuard";
import { socket } from "./mediasoup/socket";

const COUNTDOWN_SECONDS = Number(import.meta.env.VITE_COUNTDOWN_SECONDS || 10);

const ProtectedLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [forceLogoutPending, setForceLogoutPending] = useState(false);
  const [notificationCount] = useState(3);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const meetingStarted = useMeetingStore((s) => s.meetingStarted);
  const [showForceLogoutModal, setShowForceLogoutModal] = useState(false);
  const [forceLogoutMessage, setForceLogoutMessage] = useState("");
  const isAdvocateMeetingFullscreen =
    location.pathname === "/advocate-lobby" &&
    new URLSearchParams(location.search).get("fullscreen") === "1";

  // ── Session expiry warning ─────────────────────────────────────────────────
  const { showWarning, countdown, keepAlive } = useSessionGuard();

  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);
  const handleUserMenuToggle = () => setUserMenuOpen(!userMenuOpen);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleForceLogoutConfirm = async () => {
    setShowForceLogoutModal(false);
    const { userData } = useMeetingStore.getState();
    if (userData?.role === "Judge") {
      await logout();
      return;
    }
    setForceLogoutPending(true);
  };

  // ── Force logout from another device (SSE event) ───────────────────────────
  useEffect(() => {
    const handler = async (event: any) => {
      setForceLogoutMessage(event.detail);
      setShowForceLogoutModal(true);
      const { userData, selectedCase } = useMeetingStore.getState();
      if (userData?.role === "Judge") {
        if (selectedCase?.cnr) {
          socket?.emit("end-meeting", {
            roomId: userData?.roomId,
            roomNo: userData?.roomNo,
            actionBy: userData?._id,
            cnr: selectedCase?.cnr,
          });
        } else {
          await logout();
          navigate("/dashboard");
        }
      } else {
        navigate("/dashboard");
      }
    };
    window.addEventListener("force-logout", handler);
    return () => window.removeEventListener("force-logout", handler);
  }, []);

  // ── Scroll to top on route change ─────────────────────────────────────────
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  // ── Force logout pending — logout on next user activity ───────────────────
  useEffect(() => {
    if (!forceLogoutPending) return;

    const handleActivity = async () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      await logout();
    };

    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("touchstart", handleActivity);

    return () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
    };
  }, [forceLogoutPending]);

  if (!isLoggedin()) {
    return <Navigate to="/" replace />;
  }

  if (isAdvocateMeetingFullscreen) {
    return <Outlet />;
  }

  return (
    // h-dvh, not h-screen: on mobile 100vh is taller than the visible area
    // (browser toolbar), which made <main> scroll and hid page content
    <div className="flex h-dvh bg-blue-50">
      {isLoggingOut && <Loader text="Signing out..." />}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
        user={user}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64 transition-all duration-300">
        {!meetingStarted && (
          <Header
            user={user}
            notificationCount={notificationCount}
            userMenuOpen={userMenuOpen}
            setUserMenuOpen={setUserMenuOpen}
            onMenuToggle={handleSidebarToggle}
            onUserMenuToggle={handleUserMenuToggle}
            onLogout={handleLogout}
          />
        )}

        <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto bg-blue-50">
          <Outlet />
        </main>
      </div>

      {/* ── Session expiry warning modal ──────────────────────────────────── */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl w-[440px] p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    stroke="#ca8a04"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-800">
                Session Expiring Soon
              </h2>
            </div>

            {/* Message */}
            <p className="text-gray-600 leading-7">
              Your session will expire due to inactivity. You will be
              automatically logged out in{" "}
              <span className="font-bold text-red-600 tabular-nums">
                {countdown}s
              </span>
              .
            </p>

            {/* Countdown ring */}
            <div className="flex justify-center my-5">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke="#f3f4f6"
                    strokeWidth="6"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke={countdown <= 3 ? "#ef4444" : "#2563eb"}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${
                      2 * Math.PI * 28 * (1 - countdown / COUNTDOWN_SECONDS)
                    }`}
                    style={{
                      transition: "stroke-dashoffset 0.9s linear, stroke 0.3s",
                    }}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-800 tabular-nums">
                  {countdown}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={keepAlive}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Stay Logged In
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Force logout modal (another device logged in) ─────────────────── */}
      {showForceLogoutModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl w-[500px] p-6">
            <div className="flex justify-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Session Ended
              </h2>
            </div>
            <p className="text-gray-600 leading-7">{forceLogoutMessage}</p>
            <div className="flex justify-center mt-6">
              <button
                onClick={handleForceLogoutConfirm}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtectedLayout;
